import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import Modal from './Modal';
import { Btn, Input, Select } from './Input';
import { IconCheck, IconTrash, IconDoc, IconEye, IconEyeOff } from './Icons';
import { useToast } from './Toast';
import HelpHint from './HelpHint';

const TODAY = new Date().toISOString().slice(0, 10);

function Toggle({ options, value, onChange }) {
  return (
    <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--border)' }}>
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className="flex-1 px-3 py-1.5 transition-colors"
          style={value === opt.value
            ? { background: 'var(--acc)', color: '#fff', fontSize: '9px', fontWeight: 700, letterSpacing: '0.04em' }
            : { background: 'var(--bg4)', color: 'var(--ink2)', fontSize: '9px', fontWeight: 700, letterSpacing: '0.04em' }}>
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Wspólny formularz pakietu (renderowany jako Modal).
 *
 * Tryb klienta: podaj `klientId` — selektor klienta ukryty, pokazane pole nr faktury
 *               i akcje "Usuń" / "Oznacz zapłacony" w stopce.
 * Tryb globalny: podaj `klienci` (lista) — selektor klienta widoczny, bez nr faktury,
 *                bez akcji w stopce.
 */
export default function PakietForm({
  pakiet,
  klientId,
  klienci,
  onClose,
  onSaved,
  onDelete,
  onMarkPaid,
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const isEdit = !!pakiet;
  const isClientContext = klientId != null;

  const [form, setForm] = useState(isEdit ? {
    klient_id: pakiet.klient_id ?? '',
    nazwa: pakiet.nazwa ?? '',
    liczba_spacerow: pakiet.liczba_spacerow ?? 1,
    kwota: pakiet.kwota ?? '',
    miesiac_hostingu_w_cenie: pakiet.miesiac_hostingu_w_cenie ?? 0,
    stawka_hostingu_pakiet: pakiet.stawka_hostingu_pakiet ?? '',
    sposob_platnosci: pakiet.sposob_platnosci ?? 'z_gory',
    forma_platnosci: pakiet.forma_platnosci ?? 'przelew',
    status: pakiet.status ?? 'niezaplacone',
    data_sprzedazy: pakiet.data_sprzedazy?.slice(0, 10) ?? TODAY,
    data_platnosci: pakiet.data_platnosci?.slice(0, 10) ?? '',
    notatki: pakiet.notatki ?? '',
    nr_faktury: pakiet.nr_faktury ?? '',
    widoczna_w_panelu: pakiet.widoczna_w_panelu == null ? 1 : (pakiet.widoczna_w_panelu ? 1 : 0),
  } : {
    klient_id: '',
    nazwa: '',
    liczba_spacerow: 1,
    kwota: '',
    miesiac_hostingu_w_cenie: 0,
    stawka_hostingu_pakiet: '',
    sposob_platnosci: 'z_gory',
    forma_platnosci: 'przelew',
    status: 'niezaplacone',
    data_sprzedazy: TODAY,
    data_platnosci: '',
    notatki: '',
    nr_faktury: '',
    widoczna_w_panelu: 1,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Załącznik PDF — wybrany lokalnie plik (czeka na save) + nazwa istniejącego pliku
  // (gdy edytujemy pakiet, który już ma PDF wgrany).
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfFilename, setPdfFilename] = useState(pakiet?.pdf_filename || null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const pickPdfFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) { toast.error('Plik jest większy niż 10 MB'); return; }
      setPdfFile(file);
    };
    input.click();
  };

  const downloadExistingPdf = async () => {
    if (!isEdit || !pdfFilename) return;
    setPdfBusy(true);
    try { await api.downloadPakietPdf(pakiet.id, pdfFilename); }
    catch (e) { toast.error(e.message); }
    finally { setPdfBusy(false); }
  };

  const removeExistingPdf = async () => {
    if (!isEdit || !pdfFilename) return;
    setPdfBusy(true);
    try {
      await api.deletePakietPdf(pakiet.id);
      setPdfFilename(null);
      toast.success('Usunięto PDF');
    } catch (e) { toast.error(e.message); }
    finally { setPdfBusy(false); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.nazwa.trim()) return setErr(t('validation.packageNameRequired'));
    if (!form.kwota || isNaN(Number(form.kwota))) return setErr(t('validation.amountRequired'));
    setErr('');
    setSaving(true);
    try {
      const payload = {
        ...form,
        klient_id: isClientContext ? klientId : (form.klient_id || null),
        kwota: Number(form.kwota),
        liczba_spacerow: Number(form.liczba_spacerow),
        miesiac_hostingu_w_cenie: Number(form.miesiac_hostingu_w_cenie) || 0,
        stawka_hostingu_pakiet: form.stawka_hostingu_pakiet !== '' ? Number(form.stawka_hostingu_pakiet) : null,
        data_platnosci: form.status === 'zaplacone' ? (form.data_platnosci || null) : null,
        nr_faktury: isClientContext ? (form.nr_faktury?.trim() || null) : undefined,
        widoczna_w_panelu: form.widoczna_w_panelu ? 1 : 0,
      };
      let pakietId;
      if (isEdit) {
        await api.updatePakiet(pakiet.id, payload);
        pakietId = pakiet.id;
      } else {
        const created = await api.addPakiet(payload);
        pakietId = created?.id;
      }
      // Upload PDF po zapisaniu (znamy id) — analogicznie do faktur.
      if (pdfFile && pakietId) {
        try {
          await api.uploadPakietPdf(pakietId, pdfFile);
        } catch (e) {
          toast.error('Pakiet zapisany, ale upload PDF nie powiódł się: ' + e.message);
        }
      }
      toast.success(isEdit ? t('toast.saved') : t('toast.added'));
      onSaved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={isEdit ? t('clientDetail.editPackage') : t('clientDetail.newPackage')} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {!isClientContext && (
          <Select
            label={`${t('finances.client')} ${t('auth.optional')}`}
            value={form.klient_id}
            onChange={e => set('klient_id', e.target.value)}>
            <option value="">— {t('common.noData')} —</option>
            {(klienci || []).map(k => (
              <option key={k.id} value={k.id}>{k.nazwa}</option>
            ))}
          </Select>
        )}

        <Input
          label={`${t('clientDetail.packageName')} *`}
          value={form.nazwa}
          onChange={e => set('nazwa', e.target.value)}
          placeholder="np. Pakiet 10 spacerów"
        />

        <div className="grid sm:grid-cols-2 gap-3">
          <Input
            label={`${t('clientDetail.spacesCount')} *`}
            type="number"
            min="1"
            value={form.liczba_spacerow}
            onChange={e => set('liczba_spacerow', e.target.value)}
          />
          <Input
            label={`${t('clientDetail.packagePrice')} *`}
            type="number"
            min="0"
            step="0.01"
            value={form.kwota}
            onChange={e => set('kwota', e.target.value)}
            placeholder="0.00"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{t('clientDetail.hostingMonthsPerSpace')}</label>
          <input
            type="number"
            min="0"
            className="ds-input"
            value={form.miesiac_hostingu_w_cenie}
            onChange={e => set('miesiac_hostingu_w_cenie', e.target.value)}
          />
          <p style={{ fontSize: '10px', color: 'var(--ink3)' }}>{t('clientDetail.hostingMonthsHelp')}</p>
        </div>

        {Number(form.miesiac_hostingu_w_cenie) > 0 && (
          <div className="flex flex-col gap-1">
            <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{t('clientDetail.hostingRateAfter')}</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="ds-input"
              value={form.stawka_hostingu_pakiet}
              onChange={e => set('stawka_hostingu_pakiet', e.target.value)}
              placeholder="np. 29.00"
            />
          </div>
        )}

        <div className="flex flex-col gap-1">
          <span style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            {t('clientDetail.paymentMethod')}
            <HelpHint text={t('help.paymentTiming')} label={t('clientDetail.paymentMethod')} />
          </span>
          <Toggle
            options={[
              { value: 'z_gory', label: t('clientDetail.upfront') },
              { value: 'z_dolu', label: t('clientDetail.postpaid') },
            ]}
            value={form.sposob_platnosci}
            onChange={v => set('sposob_platnosci', v)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <span style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{t('clientDetail.paymentForm')}</span>
          <Toggle
            options={[
              { value: 'przelew', label: t('clientDetail.transfer') },
              { value: 'gotowka', label: t('clientDetail.cash') },
            ]}
            value={form.forma_platnosci}
            onChange={v => set('forma_platnosci', v)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <span style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{t('finances.status')}</span>
          <Toggle
            options={[
              { value: 'niezaplacone', label: t('clientDetail.unpaid') },
              { value: 'zaplacone', label: t('clientDetail.paid') },
            ]}
            value={form.status}
            onChange={v => set('status', v)}
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <Input
            label={`${t('clientDetail.saleDate')} *`}
            type="date"
            value={form.data_sprzedazy}
            onChange={e => set('data_sprzedazy', e.target.value)}
          />
          {form.status === 'zaplacone' && (
            <Input
              label={t('spaceDetail.paymentDate')}
              type="date"
              value={form.data_platnosci}
              onChange={e => set('data_platnosci', e.target.value)}
            />
          )}
        </div>

        {isClientContext && (
          <Input
            label={t('clientDetail.invoiceNumber')}
            placeholder="np. FV/2026/001"
            value={form.nr_faktury}
            onChange={e => set('nr_faktury', e.target.value)}
          />
        )}

        {/* Załącznik PDF — wgrywany po save, na edycji od razu na backend */}
        {isClientContext && (
          <div className="flex flex-col gap-1.5">
            <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>
              Załącznik PDF (skan faktury za pakiet)
            </label>
            {pdfFilename && !pdfFile ? (
              <div className="flex gap-2 flex-wrap items-center">
                <Btn type="button" variant="ghost" onClick={downloadExistingPdf} disabled={pdfBusy}>
                  <span className="flex items-center gap-1.5"><IconDoc className="w-3 h-3" /> {pdfFilename}</span>
                </Btn>
                <Btn type="button" variant="ghost" onClick={pickPdfFile} disabled={pdfBusy}>Zamień</Btn>
                <Btn type="button" variant="danger" onClick={removeExistingPdf} disabled={pdfBusy}>Usuń</Btn>
              </div>
            ) : pdfFile ? (
              <div className="flex gap-2 flex-wrap items-center">
                <span style={{ fontSize: '11px', color: 'var(--ink2)', fontWeight: 600, padding: '6px 10px', background: 'var(--bg4)', borderRadius: 'var(--r-btn)', border: '1px solid var(--border)' }}>
                  <IconDoc className="w-3 h-3 inline mr-1" /> {pdfFile.name} ({Math.round(pdfFile.size / 1024)} KB)
                </span>
                <Btn type="button" variant="ghost" onClick={() => setPdfFile(null)}>Anuluj</Btn>
                <p style={{ fontSize: '10px', color: 'var(--ink3)' }}>{isEdit ? 'Zostanie wgrany po zapisaniu zmian.' : 'Zostanie wgrany po utworzeniu pakietu.'}</p>
              </div>
            ) : (
              <div>
                <Btn type="button" variant="ghost" onClick={pickPdfFile}>
                  <span className="flex items-center gap-1.5"><IconDoc className="w-3 h-3" /> Dodaj PDF</span>
                </Btn>
                <p style={{ fontSize: '10px', color: 'var(--ink3)', marginTop: '4px' }}>
                  Klient zobaczy go w swoim panelu i będzie mógł pobrać. Max 10 MB.
                </p>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{t('clientDetail.notatki')}</label>
          <textarea
            rows={2}
            className="ds-input resize-none"
            value={form.notatki}
            onChange={e => set('notatki', e.target.value)}
            placeholder="Dodatkowe informacje..."
          />
        </div>

        {/* Widoczność w panelu klienta — togglowalny chip. Domyślnie widoczna. */}
        {isClientContext && (
          <div
            onClick={() => set('widoczna_w_panelu', form.widoczna_w_panelu ? 0 : 1)}
            style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '10px 12px', borderRadius: '7px',
              background: form.widoczna_w_panelu ? 'rgba(90,158,114,0.07)' : 'var(--bg3)',
              border: `1px solid ${form.widoczna_w_panelu ? 'rgba(90,158,114,0.3)' : 'var(--border)'}`,
              cursor: 'pointer',
            }}
          >
            {form.widoczna_w_panelu
              ? <IconEye className="w-3.5 h-3.5" style={{ color: 'var(--ok2)', flexShrink: 0 }} />
              : <IconEyeOff className="w-3.5 h-3.5" style={{ color: 'var(--ink3)', flexShrink: 0 }} />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink)', margin: 0 }}>
                {form.widoczna_w_panelu ? 'Widoczna w panelu klienta' : 'Ukryta w panelu klienta'}
              </p>
              <p style={{ fontSize: '10px', color: 'var(--ink3)', margin: '2px 0 0' }}>
                {form.widoczna_w_panelu
                  ? 'Klient zobaczy tę fakturę w swoim panelu.'
                  : 'Klient nie zobaczy tej faktury — możesz ją mieć tylko do swojej ewidencji.'}
              </p>
            </div>
            <div style={{
              width: '30px', height: '17px', borderRadius: '9px',
              background: form.widoczna_w_panelu ? 'var(--ok2)' : 'var(--bg4)',
              border: '1px solid var(--border)', position: 'relative', flexShrink: 0,
            }}>
              <div style={{
                position: 'absolute', top: '2px',
                left: form.widoczna_w_panelu ? '13px' : '2px',
                width: '11px', height: '11px', borderRadius: '50%',
                background: '#fff', transition: 'left 0.15s',
              }} />
            </div>
          </div>
        )}

        {err && <p style={{ fontSize: '10px', color: 'var(--err2)' }}>{err}</p>}

        <div className="flex justify-between items-center gap-2 pt-2">
          <div className="flex gap-2">
            {isEdit && onDelete && (
              <Btn type="button" variant="danger" onClick={onDelete}>
                <span className="flex items-center gap-1.5"><IconTrash className="w-3.5 h-3.5" /> {t('common.delete')}</span>
              </Btn>
            )}
            {isEdit && onMarkPaid && pakiet.status !== 'zaplacone' && (
              <Btn type="button" variant="success" onClick={onMarkPaid}>
                <span className="flex items-center gap-1.5"><IconCheck className="w-3.5 h-3.5" /> {t('clientDetail.markAsPaid')}</span>
              </Btn>
            )}
          </div>
          <div className="flex gap-2">
            <Btn type="button" variant="ghost" onClick={onClose}>{t('common.cancel')}</Btn>
            <Btn type="submit" disabled={saving}>
              {saving ? t('common.saving') : isEdit ? t('common.save') : t('clientDetail.addPackage')}
            </Btn>
          </div>
        </div>
      </form>
    </Modal>
  );
}
